// Storage keys, view presets, and visibility builders for the accounts table.
// Kept separate from the column defs so other tables on /accounts can reuse the
// same persistence model without dragging in JSX renderers.

import type { ColumnDef } from './accountsColumnDefs.types';

export const FILTER_STORAGE_KEY = 'atek-accounts-column-filters';
export const VISIBILITY_STORAGE_KEY = 'atek-accounts-column-visibility';
export const ORDER_STORAGE_KEY = 'atek-accounts-column-order';
export const WIDTHS_STORAGE_KEY = 'atek-accounts-column-widths';
export const PRESET_STORAGE_KEY = 'atek-accounts-view-preset';
export const SORT_STORAGE_KEY = 'atek-accounts-sort';

export const DEFAULT_COLUMN_WIDTH = 140;
export const MIN_COLUMN_WIDTH = 60;

export type ViewPreset = 'subscription' | 'operations' | 'audit' | 'all';

export const DEFAULT_PRESET: ViewPreset = 'subscription';

export const VIEW_PRESETS: Record<Exclude<ViewPreset, 'all'>, string[]> = {
  // Current default — billing / AM context
  subscription: [
    'account_number',
    'name',
    'has_calibration_contract',
    'calibrations_due',
    'overdue_calibrations',
    'contractual_manager_name',
    'sales_manager_name',
    'contract_number',
    'status',
    'sensors_qty',
    'contract_start_date',
    'contract_end_date',
    'arr',
    'actions',
  ],
  // Operational triage — zeros and 24h activity tell the story
  operations: [
    'account_number',
    'name',
    'sales_manager_name',
    'active_transmitters_count',
    'inactive_transmitters_count',
    'gateways_count',
    'sim_cards_count',
    'active_sensors_count',
    'inactive_sensors_count',
    'open_tickets_count',
    'alert_24h_count',
    'emails_24h_count',
    'calls_24h_count',
    'low_battery_devices_count',
    'overdue_calibrations',
    'days_until_subscription_expiry',
    'actions',
  ],
  // Audit — find misconfigured accounts at a glance.
  // Sales Mgr / Contractual Mgr surface via their existing name columns
  // (empty cell = unassigned). Drive/HubSpot render as clickable icon links.
  audit: [
    'account_number',
    'name',
    'status',
    'sales_manager_name',
    'contractual_manager_name',
    'technical_manager_name',
    'it_manager_name',
    'has_drive_folder',
    'has_qb_customer_id',
    'has_hubspot_company_id',
    'total_invoices_count',
    'active_transmitters_count',
    'active_sensors_count',
    'actions',
  ],
};

export const VIEW_PRESET_LABELS: Record<ViewPreset, string> = {
  subscription: 'Subscription',
  operations: 'Operations',
  audit: 'Audit',
  all: 'All columns',
};

export function buildPresetVisibility<T>(
  preset: ViewPreset,
  columns: ColumnDef<T>[],
): Record<string, boolean> {
  const visibility: Record<string, boolean> = {};
  if (preset === 'all') {
    for (const col of columns) visibility[col.key] = true;
    return visibility;
  }
  const set = new Set(VIEW_PRESETS[preset]);
  for (const col of columns) visibility[col.key] = set.has(col.key);
  return visibility;
}

export function isViewPreset(value: unknown): value is ViewPreset {
  return value === 'subscription' || value === 'operations' || value === 'audit' || value === 'all';
}
